// frontend/src/pages/Tracking.jsx
import React, { useState, useEffect } from "react";
import { Search, Package, Clock, MapPin, CheckCircle, AlertCircle, Truck, RefreshCw, Plus } from "lucide-react";
import trackingAPI from "../api/trackingAPI";
import "./Tracking.css";

const Tracking = () => {
  const [awbNumber, setAwbNumber] = useState("");
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [trackingHistory, setTrackingHistory] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Add AWB form state
  const [newAWB, setNewAWB] = useState({
    awbNumber: "",
    orderId: "",
    customerName: "",
    product: "",
    courier: "",
    dispatchDate: "",
    expectedDelivery: "",
    location: ""
  });

  const handleTrackAWB = async () => {
    if (!awbNumber.trim()) {
      setError("Please enter AWB number");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [tracking, history] = await Promise.all([
        trackingAPI.trackAWB(awbNumber),
        trackingAPI.getAWBHistory(awbNumber)
      ]);
      
      setTrackingData(tracking);
      setTrackingHistory(history);
    } catch (err) {
      setError("Failed to track AWB. Please check the number and try again.");
      setTrackingData(null);
      setTrackingHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAWB = async () => {
    if (!newAWB.awbNumber || !newAWB.orderId || !newAWB.customerName || !newAWB.product || !newAWB.courier) {
      alert("Please fill all required fields");
      return;
    }

    try {
      await trackingAPI.createAWBTracking(newAWB);
      setShowAddForm(false);
      setNewAWB({
        awbNumber: "",
        orderId: "",
        customerName: "",
        product: "",
        courier: "",
        dispatchDate: "",
        expectedDelivery: "",
        location: ""
      });
      alert("AWB tracking created successfully!");
    } catch (error) {
      console.error('Error creating AWB:', error);
      alert("Failed to create AWB. Please try again.");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "dispatched": return <Truck className="icon-blue" />;
      case "in-transit": return <Package className="icon-orange" />;
      case "out-for-delivery": return <MapPin className="icon-purple" />;
      case "delivered": return <CheckCircle className="icon-green" />;
      case "returned": return <RefreshCw className="icon-red" />;
      default: return <AlertCircle className="icon-gray" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="tracking-container">
      <div className="tracking-header">
        <div className="header-left">
          <h1 className="tracking-title">AWB Tracking System</h1>
          <p className="tracking-subtitle">Track your shipments from dispatch to return</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>
            <Plus size={18} />
            Add AWB
          </button>
        </div>
      </div>

      <div className="tracking-search-card">
        <div className="search-input-group">
          <div className="input-wrapper">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Enter AWB Number (e.g., AWB123456789)"
              value={awbNumber}
              onChange={(e) => setAwbNumber(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleTrackAWB()}
              className="tracking-input"
            />
          </div>
          <button 
            onClick={handleTrackAWB}
            disabled={loading}
            className="track-button"
          >
            {loading ? "Tracking..." : "Track Shipment"}
          </button>
        </div>
        
        {error && <div className="error-message">{error}</div>}
      </div>

      {trackingData && (
        <div className="tracking-results">
          <div className="tracking-status-card">
            <div className="status-header">
              <h3>Current Status</h3>
              <div className="status-badge status-{trackingData.status}">
                {getStatusIcon(trackingData.status)}
                <span>{trackingData.status?.replace('-', ' ').toUpperCase()}</span>
              </div>
            </div>
            
            <div className="tracking-details">
              <div className="detail-row">
                <span className="label">AWB Number:</span>
                <span className="value">{trackingData.awbNumber}</span>
              </div>
              <div className="detail-row">
                <span className="label">Order ID:</span>
                <span className="value">{trackingData.orderId}</span>
              </div>
              <div className="detail-row">
                <span className="label">Customer:</span>
                <span className="value">{trackingData.customerName}</span>
              </div>
              <div className="detail-row">
                <span className="label">Product:</span>
                <span className="value">{trackingData.product}</span>
              </div>
              <div className="detail-row">
                <span className="label">Courier:</span>
                <span className="value">{trackingData.courier}</span>
              </div>
              <div className="detail-row">
                <span className="label">Dispatch Date:</span>
                <span className="value">{formatDate(trackingData.dispatchDate)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Expected Delivery:</span>
                <span className="value">{formatDate(trackingData.expectedDelivery)}</span>
              </div>
            </div>
          </div>

          <div className="tracking-timeline">
            <h3>Tracking Timeline</h3>
            <div className="timeline">
              {trackingHistory.map((event, index) => (
                <div key={index} className="timeline-item">
                  <div className="timeline-marker">
                    {getStatusIcon(event.status)}
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-header">
                      <h4>{event.status?.replace('-', ' ').toUpperCase()}</h4>
                      <span className="timeline-time">{formatDate(event.timestamp)}</span>
                    </div>
                    <p className="timeline-description">{event.description}</p>
                    {event.location && (
                      <div className="timeline-location">
                        <MapPin size={14} />
                        <span>{event.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {trackingData.status === 'returned' && (
            <div className="return-notice">
              <AlertCircle className="notice-icon" />
              <div>
                <h4>Product Returned</h4>
                <p>This product has been returned and is now available in the Customer Returns section.</p>
                <button className="view-return-button">
                  View in Customer Returns
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add AWB Modal */}
      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal-card add-awb-modal">
            <div className="modal-header">
              <h3>Add New AWB Tracking</h3>
              <button className="btn-close" onClick={() => setShowAddForm(false)}>
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>AWB Number *</label>
                  <input
                    type="text"
                    value={newAWB.awbNumber}
                    onChange={(e) => setNewAWB({...newAWB, awbNumber: e.target.value})}
                    placeholder="e.g., AWB123456789"
                  />
                </div>
                
                <div className="form-group">
                  <label>Order ID *</label>
                  <input
                    type="text"
                    value={newAWB.orderId}
                    onChange={(e) => setNewAWB({...newAWB, orderId: e.target.value})}
                    placeholder="e.g., ORD-001"
                  />
                </div>
                
                <div className="form-group">
                  <label>Customer Name *</label>
                  <input
                    type="text"
                    value={newAWB.customerName}
                    onChange={(e) => setNewAWB({...newAWB, customerName: e.target.value})}
                    placeholder="Customer name"
                  />
                </div>
                
                <div className="form-group">
                  <label>Product *</label>
                  <input
                    type="text"
                    value={newAWB.product}
                    onChange={(e) => setNewAWB({...newAWB, product: e.target.value})}
                    placeholder="Product name"
                  />
                </div>
                
                <div className="form-group">
                  <label>Courier *</label>
                  <select
                    value={newAWB.courier}
                    onChange={(e) => setNewAWB({...newAWB, courier: e.target.value})}
                  >
                    <option value="">Select Courier</option>
                    <option value="Blue Dart">Blue Dart</option>
                    <option value="FedEx">FedEx</option>
                    <option value="XpressBees">XpressBees</option>
                    <option value="Delhivery">Delhivery</option>
                    <option value="DTDC">DTDC</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={newAWB.location}
                    onChange={(e) => setNewAWB({...newAWB, location: e.target.value})}
                    placeholder="Current location"
                  />
                </div>
                
                <div className="form-group">
                  <label>Dispatch Date</label>
                  <input
                    type="date"
                    value={newAWB.dispatchDate}
                    onChange={(e) => setNewAWB({...newAWB, dispatchDate: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Expected Delivery</label>
                  <input
                    type="date"
                    value={newAWB.expectedDelivery}
                    onChange={(e) => setNewAWB({...newAWB, expectedDelivery: e.target.value})}
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleCreateAWB}>
                Create AWB
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tracking;
